"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DependencyFieldLogic = void 0;
const types_1 = require("@smartsuite/types");
const clipboard_1 = require("../../clipboard/clipboard");
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const comparisons_1 = require("../../comparisons");
const field_logic_1 = require("../../field-type-registry/field-logic");
const array_1 = require("../../helpers/array");
const mapping_1 = require("../../helpers/mapping");
const dependency_fieldtype_1 = require("../../safe-field-value/reference-fields/dependency-field/dependency.fieldtype");
const intl_1 = require("../../locale/intl");
const texts_1 = require("../../texts");
const dom_1 = require("../../utils/dom");
const fields_1 = require("../../utils/fields");
const record_1 = require("../../utils/record");
const helpers_1 = require("./helpers");
class DependencyFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "dependencyfield";
        this.label = (0, intl_1.$t)(texts_1.dependencyFieldText.dependency);
        this.icon = "linked-record";
        this.iconColor = "#3A86FF";
        this.placeholder = "";
        this.uniqueByApplication = true;
        this.fieldDisabledMessage = (0, intl_1.$t)(texts_1.dependencyFieldText.onlyOneField);
        this.fieldValueGetter = new dependency_fieldtype_1.DependencyFieldValueGetter();
        this.clipboardPasteRules = {
            dependencyfield: clipboard_1.clipboardDependencyToSelf,
        };
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.contains,
            not_contains: comparisons_1.notContains,
            has_all_of: comparisons_1.hasAllOf,
            has_any_of: comparisons_1.hasAnyOf,
            is_exactly: comparisons_1.isExactly,
        };
        this.aggregates = {};
    }
    getAppFieldSlug() {
        return "dependency";
    }
    getEmptyValue() {
        return {
            predecessor: [],
            successor: [],
        };
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return (0, react_fast_compare_1.default)(fieldValue1, fieldValue2);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    isFieldExpandableOnRecordModal() {
        return true;
    }
    formatActivityRecordChangeValues(previousValue, currentValue, extraData) {
        const previousValueDiff = (0, helpers_1.dependencyValueDifference)(previousValue, currentValue);
        const currentValueDiff = (0, helpers_1.dependencyValueDifference)(currentValue, previousValue);
        const previousValueHydrated = (0, helpers_1.hydrateDependencyValue)(previousValueDiff, extraData);
        const currentValueHydrated = (0, helpers_1.hydrateDependencyValue)(currentValueDiff, extraData);
        return {
            previousValue: previousValueHydrated,
            currentValue: currentValueHydrated,
        };
    }
    valueToString(fieldValue) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.predecessor) && !(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.successor)) {
            return "";
        }
        const relationIds = (0, helpers_1.getUniqueRecordsIds)(fieldValue);
        const records = this.appRecordsService.getMultipleRelatedRecords((0, array_1.shallowUnique)(relationIds));
        return this.recordsToString(records);
    }
    recordsToString(records) {
        return records
            .map((item) => (0, record_1.getTitleOrUntitledRecordString)(item))
            .join(", ");
    }
    getViewControlRules(field) {
        return {
            ...super.getViewControlRules(field),
            nestedFieldsInFieldsToDisplay: "show",
            fieldInSort: "hide",
            nestedFieldsInSort: "hide",
            nestedFieldsInFilter: "hide",
            fieldInGroup: "show",
            nestedFieldsInGroup: "hide",
            fieldInSpotlight: "hide",
            nestedFieldsInSpotlight: "hide",
            disableFieldInTextSearch: "hide",
        };
    }
    getInlineEditRules(field) {
        const baseInlineEditRules = super.getInlineEditRules(field);
        return {
            ...baseInlineEditRules,
            editorWidth: "100%",
            switchToInputControl: "OnCellSelect",
            editOnStartTyping: false,
            saveKeys: types_1.navigationKeys,
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnModalOpen",
            allowAddRecordToGroup: false,
            allowEditNestedFields: true,
        };
    }
    processFilterValue(filterValue, comparison) {
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return null;
        }
        return filterValue;
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = (0, fields_1.getValueFromRecordByField)(record, field);
        const relationIDs = (0, helpers_1.getUniqueRecordsIds)(fieldValue);
        const relatedRecords = this.appRecordsService
            .getMultipleRelatedRecords(relationIDs)
            .filter(record_1.isRecordNotDeleted);
        const relatedIDs = relatedRecords.map(({ id }) => id);
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return relatedIDs;
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return this.recordsToString(relatedRecords);
        }
        if (!relatedIDs) {
            return [];
        }
        return relatedIDs;
    }
    filterValueToString(filterValue, field, userPreferences, comparison) {
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return "";
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return String(filterValue);
        }
        if (!Array.isArray(filterValue)) {
            return "";
        }
        return this.appRecordsService
            .getMultipleRelatedRecords((0, array_1.shallowUnique)(filterValue))
            .filter(record_1.isRecordNotDeleted)
            .map((item) => (0, record_1.getTitleOrUntitledRecordString)(item))
            .join(", ");
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            placeholder: comparison === "contains" || comparison === "not_contains"
                ? (0, intl_1.$t)(texts_1.linkedRecordFieldText.enter_record)
                : (0, intl_1.$t)(texts_1.linkedRecordFieldText.select_record),
        };
    }
    getGroupValues(fieldValue) {
        var _a, _b;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.predecessor) === null || _a === void 0 ? void 0 : _a.length) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.successor) === null || _b === void 0 ? void 0 : _b.length)) {
            return [];
        }
        const uniqueIds = (0, helpers_1.getUniqueRecordsIds)(fieldValue);
        const recordsMap = (0, mapping_1.arrayToMap)(this.appRecordsService
            .getMultipleRelatedRecords(uniqueIds)
            .filter(record_1.isRecordNotDeleted), "id");
        const { predecessor = [], successor = [] } = fieldValue;
        const predecessorValues = predecessor
            .filter(({ record }) => recordsMap.has(record))
            .map((relation) => ({
            predecessor: [relation],
            successor: [],
        }));
        const successorValues = successor
            .filter(({ record }) => recordsMap.has(record))
            .map((relation) => ({
            predecessor: [],
            successor: [relation],
        }));
        return [...predecessorValues, ...successorValues];
    }
    /**
     * Returns a concatenated string of IDs.
     * Group by ID to avoid having different entries in the same group if the title is equal.
     *
     * @param fieldValue    value from a record
     */
    groupByKey(fieldValue) {
        var _a, _b;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.predecessor) === null || _a === void 0 ? void 0 : _a.length) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.successor) === null || _b === void 0 ? void 0 : _b.length)) {
            return "";
        }
        const { predecessor = [], successor = [] } = fieldValue;
        const allIds = predecessor
            .map(({ record }) => record)
            .concat(successor.map(({ record }) => record));
        return allIds.join(", ");
    }
    sortKey(fieldValue) {
        var _a, _b;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.predecessor) === null || _a === void 0 ? void 0 : _a.length) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.successor) === null || _b === void 0 ? void 0 : _b.length)) {
            return "";
        }
        const relationIds = (0, helpers_1.getUniqueRecordsIds)(fieldValue).join(", ");
        return `${this.valueToString(fieldValue)}, ${relationIds}`;
    }
    canChangeValue() {
        return false;
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        var _a, _b, _c, _d;
        const pillPaddings = 36; // 4 paddings by 9px
        const predecessorCount = (_b = (_a = fieldValue.predecessor) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        const successorCount = (_d = (_c = fieldValue.successor) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
        const pillsText = `Predecessors: ${predecessorCount} Successors: ${successorCount}`;
        let size = (0, dom_1.getTextSize)(pillsText, undefined, font);
        size += pillPaddings;
        return size;
    }
}
exports.DependencyFieldLogic = DependencyFieldLogic;
